import { ExecuteParameters, ExtensionBootstrapInfo, ExtensionMode, VerbId } from '@tableau/api-internal-contract-js';
import * as CryptoJS from 'crypto-js';
import * as guid from 'guid';
import { ExtensionPermission } from '../../gen-src/preslayer/Enums';
import { AddInInstancePresModel } from '../ApiCore';
import { PresLayerOptionalCompatibility } from '../mappings/PresLayerOptionalCompatibility';
import {
  FunctionCallRuntimeData,
  FunctionResults,
  InternalInitializationFields,
  RunFields,
  SharedInitializationFields,
  TimingFields,
  defaultErrorCode,
  functionComplete,
  getBrowser,
  reportEvent,
} from './TelemetryDispatcher';

const instanceIdToRunId = new Map();
type InitializationFields = ExtensionInitializationFields &
  SharedInitializationFields &
  InternalInitializationFields &
  TimingFields &
  RunFields;

/**
 * Extension specific telemetry data
 */
interface ExtensionInitializationFields {
  extension_id?: string;
  extension_id_hash2?: string;
  extension_url_hash?: string;
  extension_instance_id?: string;
  is_dialog?: boolean;
  is_sandboxed?: boolean;
  is_first_class?: boolean;
  requires_full_data?: boolean;
  extensions_api_version?: string;
}

export function telemetryFunctionComplete(
  verb: VerbId,
  timingData: FunctionCallRuntimeData,
  parameters: ExecuteParameters,
  results: FunctionResults,
  extensionInstancePresModel: AddInInstancePresModel,
): void {
  try {
    const extensionInstanceId = extensionInstancePresModel.addInLocatorPresModel.addInInstanceId;

    const timingFields = timingData.getRuntimeCalculations();

    switch (verb) {
      case VerbId.InitializeExtension:
        initializeExtensionComplete(timingFields, results, extensionInstancePresModel);
        break;
      case VerbId.RaiseHoverTupleNotification:
        break;
      default:
        // function calls
        if (!instanceIdToRunId.get(extensionInstanceId)) {
          // we haven't initialized, so there's no runid atm.
          return;
        }

        functionComplete(verb, timingFields, parameters, results, instanceIdToRunId.get(extensionInstanceId));
        break;
    }
  } catch {
    // Ignore any telemetry error
  }
}

function initializeExtensionComplete(
  timing: TimingFields,
  results: FunctionResults,
  extensionInstancePresModel: AddInInstancePresModel,
): void {
  const fields: InitializationFields = timing;

  const extensionInstanceId = extensionInstancePresModel.addInLocatorPresModel.addInInstanceId;

  // Create a new run-id per initialize - reload or a second initialize call will reset the run-id
  fields.run_id = guid.raw();

  //// Platform detail capture ////
  // Here we capture only browsers which have known hostnames and we actually support:
  //  chrome, firefox, edge, safari, unsupported
  fields.browser = getBrowser();

  if (results.isSuccessResult()) {
    const bootstrapPresmodel: ExtensionBootstrapInfo = results.model as ExtensionBootstrapInfo;

    fields.lang = bootstrapPresmodel.extensionEnvironment.extensionLanguage;
    fields.locale = bootstrapPresmodel.extensionEnvironment.extensionLocale;

    //// Extensions info ////

    // TODO this doesn't work
    fields.extensions_api_version = bootstrapPresmodel.extensionEnvironment.apiVersion;

    fields.is_authoring = bootstrapPresmodel.extensionEnvironment.extensionMode === ExtensionMode.Authoring;
    fields.is_dialog = !!bootstrapPresmodel.extensionDialogPayload;
  }

  const addInRegistrationPresModel = PresLayerOptionalCompatibility.optionalToRequiredUnsafeCast(
    extensionInstancePresModel.addInRegistrationPresModel,
  );

  fields.requires_full_data =
    addInRegistrationPresModel.extensionPermissions && addInRegistrationPresModel.extensionPermissions[0] === ExtensionPermission.FullData;

  fields.is_sandboxed = addInRegistrationPresModel.extensionIsSandboxed;
  fields.is_first_class = addInRegistrationPresModel.extensionIsFirstclass;

  if (fields.is_sandboxed || fields.is_first_class) {
    fields.extension_id = extensionInstancePresModel.addInId;
  }

  fields.extension_instance_id = extensionInstanceId;

  fields.extension_url_hash = CryptoJS.SHA256(addInRegistrationPresModel.url).toString(CryptoJS.enc.Base64);
  fields.extension_id_hash2 = CryptoJS.SHA256(extensionInstancePresModel.addInId).toString(CryptoJS.enc.Base64);

  // fields.error_code
  if (results.isErrorResult()) {
    // NOTE: We are not passing through an error code, because we don't have one at this
    //  point in time. For more fidelity in error information, we'll probably plumb through
    //  the error code from the error that was thrown on cpp side. It's a security issue
    //  to report the full error message, because it contains customer data.
    fields.error_code = defaultErrorCode;
  }

  //// Report event ////
  reportEvent('DashboardExtensionInitialized', fields).then().catch();

  instanceIdToRunId.set(fields.extension_instance_id, fields.run_id);
}
